import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
} from "../../ipd/utils";
import { TRIAGE, TRIAGE_ORDER } from "../constants";
import { triageEmergencyVisit } from "../api/emergencyApi";
import { openEmergencyBilling } from "../api/emergencyBillingApi";

/**
 * Assigning a severity category (ER-TRI-002).
 *
 * The four categories are shown as large targets in severity order rather than
 * a dropdown: this is chosen at the bedside, often in a hurry, and the colour
 * is the thing being recognised. Re-triage says plainly that the earlier
 * category is kept, because a nurse changing Green to Red needs to know they
 * are adding to the record rather than erasing what a colleague decided.
 */
export default function TriageDialog({ open, visit, onClose, onTriaged }) {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isRetriage = Boolean(visit?.triage_category);

  useEffect(() => {
    if (open) {
      setSelected(visit?.triage_category || null);
      setNote("");
      setError("");
    }
  }, [open, visit]);

  const submit = async () => {
    if (!selected) {
      setError("Select a category.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await triageEmergencyVisit(visit.id, {
        category: selected,
        note: note.trim(),
      });

      // Triage is where a queue of arrivals becomes accepted patients, so it is
      // where the visit's billing account is opened — a walk-out who was never
      // triaged should not leave an empty account behind.
      //
      // Deliberately not awaited. Opening the account is three calls to the
      // billing service and takes about four seconds; triage is done at the
      // bedside and must not wait on it. The call is idempotent, and charge
      // capture opens the account itself if this never landed, so the worst case
      // of a failure here is the delay we were avoiding.
      openEmergencyBilling(visit.id).catch(() => {});

      onTriaged?.(updated);
      onClose();
    } catch (e) {
      setError(e?.message || "Could not record the triage");
    } finally {
      setSaving(false);
    }
  };

  if (!visit) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          {isRetriage ? "Re-triage patient" : "Triage patient"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {isRetriage
            ? "The earlier category is kept — the record shows who changed it and when."
            : `Assign a severity category for ${visit.display_name}.`}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "grid", gap: 1 }} role="radiogroup" aria-label="Triage category">
          {TRIAGE_ORDER.map((key) => {
            const t = TRIAGE[key];
            const active = selected === key;
            const current = visit.triage_category === key;
            return (
              <Box
                key={key}
                component="button"
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  bgcolor: active ? t.bg : IPD_COLORS.cardBg,
                  border: `2px solid ${active ? t.color : IPD_COLORS.border}`,
                  transition: "all .12s ease",
                  "&:hover": { borderColor: t.color },
                  "&:focus-visible": { outline: `2px solid ${t.color}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: t.color, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
                    {t.label}
                    {current && (
                      <Typography component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: IPD_COLORS.textSecondary }}>
                        current
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                    {t.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Note (optional)</Typography>
          <TextField
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={isRetriage ? "What changed?" : "Anything worth recording with this assessment"}
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={ipdElevatedInputSx}
            inputProps={{ "aria-label": "Triage note" }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <IpdButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </IpdButton>
        <IpdButton onClick={submit} loading={saving}>
          {isRetriage ? "Save re-triage" : "Save triage"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
